// Simple CRUD pages for Testimonials, FAQs, Gallery — light table editors.
import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Save, UploadCloud, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "./ImageUploader";
import { uploadMediaFile } from "@/lib/media";

function BulkImageUpload({ collection, onDone }) {
  const fileRef = useRef(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, failed: 0 });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileRef.current) fileRef.current.value = "";
    if (!files.length) return;

    setRunning(true);
    setProgress({ done: 0, total: files.length, failed: 0 });
    let done = 0, failed = 0;

    // Upload sequentially so we get reliable progress + don't hammer storage.
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        const url = await uploadMediaFile(f);
        await api.post(`/admin/${collection}`, {
          url,
          alt: f.name.replace(/\.[a-z0-9]+$/i, "").replace(/[-_]+/g, " "),
          category: "general",
          is_published: true,
          order: 999,
        });
        done += 1;
      } catch (err) {
        failed += 1;
      }
      setProgress({ done, total: files.length, failed });
    }

    setRunning(false);
    if (failed === 0) toast.success(`${done} image${done === 1 ? "" : "s"} added to gallery`);
    else if (done === 0) toast.error(`Upload failed for all ${failed} files`);
    else toast.warning(`${done} added, ${failed} failed`);
    onDone?.();
  };

  const pct = progress.total ? Math.round(((progress.done + progress.failed) / progress.total) * 100) : 0;

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onFiles}
        data-testid="bulk-upload-input"
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        disabled={running}
        className="inline-flex items-center gap-2 bg-white border border-zinc-300 text-zinc-800 px-4 py-2 text-sm rounded-md hover:bg-zinc-50 disabled:opacity-60"
        data-testid="bulk-upload-btn"
        title="Select multiple images to add to the gallery at once"
      >
        {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
        {running ? `Uploading ${progress.done + progress.failed}/${progress.total}… ${pct}%` : "Upload multiple"}
      </button>
    </>
  );
}

function ListEditor({ title, collection, columns, defaultItem, bulkAction }) {
  const [items, setItems] = useState([]);
  const load = () => api.get(`/admin/${collection}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    const { data } = await api.post(`/admin/${collection}`, defaultItem());
    setItems([...items, data]);
  };
  const update = async (it) => {
    await api.put(`/admin/${collection}/${it.id}`, it);
    toast.success("Saved");
  };
  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    await api.delete(`/admin/${collection}/${id}`);
    setItems(items.filter((x) => x.id !== id));
  };
  const setLocal = (i, k, v) => {
    const next = [...items]; next[i] = { ...next[i], [k]: v }; setItems(next);
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{title}</div>
          <h1 className="font-display text-4xl mt-3">{title}</h1>
        </div>
        <div className="flex items-center gap-2">
          {bulkAction && <BulkImageUpload collection={collection} onDone={load} />}
          <button onClick={create} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm rounded-md" data-testid={`new-${collection}`}><Plus className="w-4 h-4" /> Add</button>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        {items.map((it, i) => (
          <div key={it.id} className="bg-white border border-zinc-200 rounded-md p-4 space-y-3" data-testid={`${collection}-row-${i}`}>
            {columns.map((c) => {
              if (c.type === "image") return <ImageUploader key={c.key} label={c.label} value={it[c.key]} onChange={(v) => setLocal(i, c.key, v)} testId={`${collection}-${i}-${c.key}`} />;
              if (c.type === "textarea") return <Textarea key={c.key} value={it[c.key] || ""} onChange={(e) => setLocal(i, c.key, e.target.value)} placeholder={c.label} rows={c.rows || 3} />;
              return <Input key={c.key} value={it[c.key] || ""} onChange={(e) => setLocal(i, c.key, e.target.value)} placeholder={c.label} type={c.inputType || "text"} />;
            })}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!it.is_published} onCheckedChange={(v) => setLocal(i, "is_published", v)} /> Published</label>
              <div className="flex gap-2">
                <button onClick={() => update(it)} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 bg-zinc-900 text-white rounded-md"><Save className="w-3 h-3" /> Save</button>
                <button onClick={() => remove(it.id)} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-md"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-zinc-500 p-6 bg-white border border-zinc-200 rounded-md">No items yet.</div>}
      </div>
    </div>
  );
}

export function Testimonials() {
  return <ListEditor
    title="Testimonials"
    collection="testimonials"
    defaultItem={() => ({ name: "", text: "", rating: 5, source: "", image_url: "", is_published: true, order: 999 })}
    columns={[
      { key: "name", label: "Name" },
      { key: "text", label: "Quote", type: "textarea" },
      { key: "source", label: "Source (e.g. Google, Real Wedding)" },
      { key: "rating", label: "Rating (1-5)", inputType: "number" },
      { key: "image_url", label: "Image", type: "image" },
    ]}
  />;
}

export function FAQs() {
  return <ListEditor
    title="FAQs"
    collection="faqs"
    defaultItem={() => ({ question: "", answer: "", category: "general", is_published: true, order: 999 })}
    columns={[
      { key: "question", label: "Question" },
      { key: "answer", label: "Answer", type: "textarea", rows: 4 },
      { key: "category", label: "Category" },
    ]}
  />;
}

export function Gallery() {
  return <ListEditor
    title="Gallery"
    collection="gallery"
    bulkAction
    defaultItem={() => ({ url: "", alt: "", category: "general", is_published: true, order: 999 })}
    columns={[
      { key: "url", label: "Image", type: "image" },
      { key: "alt", label: "Alt text" },
      { key: "category", label: "Category" },
    ]}
  />;
}
